import { IVacationBidCrewImportRow, IVacationBidCrewListItem } from "@vmosolutions/oc-interfaces";

import {
  ICsvFile,
  ICsvIssue,
  ICsvParseResult,
  missingHeaders,
  parseCsvWholeNumber,
  toCsvText,
} from "src/utils/csvFile";

const EMPLOYEE_ID = "employeeid";
const VACATION_DAYS = "vacationdays";
const STAT_DAYS = "statdays";

/** The columns a crew entitlement file must carry. */
export const CREW_CSV_REQUIRED_HEADERS = [EMPLOYEE_ID, VACATION_DAYS, STAT_DAYS];

/**
 * Reads a crew entitlement file into rows.
 *
 * Only the file's own shape is checked here. Whether an employee id names anyone, and whether that
 * crew member is active or already in the package, is decided by the server - the browser's crew
 * list can be stale or filtered, so a match made here would not be worth trusting.
 */
export const crewFromCsv = (file: ICsvFile): ICsvParseResult<IVacationBidCrewImportRow> => {
  if (file.fatal) return { rows: [], issues: [], fatal: file.fatal };

  const headerProblem = missingHeaders(file, CREW_CSV_REQUIRED_HEADERS);
  if (headerProblem) return { rows: [], issues: [], fatal: headerProblem };

  const issues: ICsvIssue[] = [];
  const rows: IVacationBidCrewImportRow[] = [];
  // One crew member cannot hold two entitlements in the same package, so a repeat is a mistake in
  // the file rather than something to apply twice.
  const lineByEmployeeId = new Map<string, number>();

  for (const row of file.rows) {
    const { line, values } = row;

    const employeeId = values[EMPLOYEE_ID] ?? "";
    if (!employeeId) {
      issues.push({ line, message: "Employee ID is blank." });
      continue;
    }

    const vacationDays = parseCsvWholeNumber(values[VACATION_DAYS]);
    if (vacationDays === undefined) {
      issues.push({
        line,
        message: `Vacation days "${values[VACATION_DAYS] ?? ""}" is not a whole number of zero or more.`,
      });
      continue;
    }

    const statDays = parseCsvWholeNumber(values[STAT_DAYS]);
    if (statDays === undefined) {
      issues.push({ line, message: `Stat days "${values[STAT_DAYS] ?? ""}" is not a whole number of zero or more.` });
      continue;
    }

    const firstLine = lineByEmployeeId.get(employeeId);
    if (firstLine !== undefined) {
      issues.push({ line, message: `Employee ID ${employeeId} is already listed on line ${firstLine}.` });
      continue;
    }
    lineByEmployeeId.set(employeeId, line);

    rows.push({ employeeId, vacationDays, statDays });
  }

  return { rows, issues };
};

/**
 * The columns an exported entitlement file carries, in the spelling it writes them.
 *
 * `normalizeHeader` maps each of these to the key {@link crewFromCsv} looks for, so a file written
 * here is one it reads back. A test holds the two together, since a rename on either side would
 * otherwise break the round trip silently. The names are there for whoever edits the file; reading
 * it back ignores them.
 */
export const CREW_CSV_HEADERS = ["employeeId", "firstName", "lastName", "vacationDays", "statDays"];

/** Writes crew entitlements in the format {@link crewFromCsv} reads. */
export const crewToCsv = (rows: IVacationBidCrewListItem[]): string =>
  toCsvText(
    CREW_CSV_HEADERS,
    rows.map((row) => [
      row.employeeId ?? "",
      row.firstName ?? "",
      row.lastName ?? "",
      String(row.vacationDays ?? 0),
      String(row.statDays ?? 0),
    ])
  );
